import { PendingButton } from "@/components/common/PendingButton/PendingButton"
import { toast } from "@/components/common/Toast"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { toastApiError } from "@/features/material-compliance/api/apiError"
import type { InquiryTemplateSettings } from "@/features/material-compliance/components/supplier-declarations/inquiry/settings/templateSettingsMock"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  useOrganization,
  useUpdateOrganization,
  type BusinessRole,
  type Organization,
} from "@/services/api/organizationApi"
import { useState } from "react"
import { OrgLogoSection } from "../../components/OrgLogoSection"
import { SavedMark } from "../../components/SavedMark"
import {
  SettingsField,
  SettingsPage,
  SettingsSection,
  settingsHintId,
} from "../../components/SettingsPage"
import {
  SettingsLoadError,
  SettingsSkeleton,
} from "../../components/SettingsStates"
import { TemplateSaveButton } from "../../components/TemplateSaveButton"
import { TemplateSettingsLoader } from "../../components/TemplateSettingsLoader"
import { useUnsavedChanges } from "../../components/UnsavedChanges"
import { useSavedDraft } from "../../hooks/useSavedDraft"
import { useTemplateSettingsSave } from "../../hooks/useTemplateSettingsSave"
import { companyDetailsNeedSetup } from "../../setup/setupChecks"
import { useMaterialComplianceOpen } from "../../useSettingsVisibility"

// The Select has no empty value, so "not set" gets its own.
const BUSINESS_ROLE_NOT_SET = "not-set"
const BUSINESS_ROLES: { value: BusinessRole; label: TranslationKeys }[] = [
  {
    value: "manufacturer",
    label: "SETTINGS/GENERAL/BUSINESS_ROLE_MANUFACTURER",
  },
  { value: "distributor", label: "SETTINGS/GENERAL/BUSINESS_ROLE_DISTRIBUTOR" },
  { value: "both", label: "SETTINGS/GENERAL/BUSINESS_ROLE_BOTH" },
]

/** Name, address and (for Material Compliance) business role, saved together.
 *  Mounted once the organization has loaded, so the form starts from the saved
 *  values and a background refetch never overwrites an edit in progress. */
function OrganizationDetailsSection({
  org,
  showBusinessRole,
}: {
  org: Organization
  showBusinessRole: boolean
}) {
  const { translate } = useTranslation()
  const update = useUpdateOrganization()
  const [name, setName] = useSavedDraft(org.name ?? "")
  const [address, setAddress] = useSavedDraft(org.addressMultiline ?? "")
  const savedRole = org.businessRole ?? BUSINESS_ROLE_NOT_SET
  const [role, setRole] = useSavedDraft<string>(savedRole)
  const [saved, setSaved] = useState(false)

  const dirty =
    name.trim() !== (org.name ?? "") ||
    address !== (org.addressMultiline ?? "") ||
    (showBusinessRole && role !== savedRole)
  useUnsavedChanges(dirty)

  async function onSave() {
    const trimmedName = name.trim()
    try {
      await update.mutateAsync({
        id: org.id,
        body: {
          name: trimmedName,
          addressMultiline: address || null,
          // Only sent where the field is shown, so other orgs never touch it.
          ...(showBusinessRole && {
            businessRole:
              role === BUSINESS_ROLE_NOT_SET ? null : (role as BusinessRole),
          }),
        },
      })
      // Match what was saved, so the form isn't left dirty by the trim.
      setName(trimmedName)
      setSaved(true)
      toast.success(translate("MC_ORG_SETTINGS_TOAST_SAVED"))
    } catch (error) {
      toastApiError(error, translate, "MC_ORG_SETTINGS_TOAST_SAVE_ERR")
    }
  }

  return (
    <SettingsSection
      title={translate("SETTINGS/GENERAL/DETAILS_TITLE")}
      target="org.details"
      description={translate("SETTINGS/GENERAL/DETAILS_DESCRIPTION")}
      needsSetup={companyDetailsNeedSetup(org)}
      footer={
        <>
          <PendingButton
            pending={update.isPending}
            disabled={!dirty || !name.trim()}
            onClick={onSave}
          >
            {translate("MC_ORG_SETTINGS_SAVE")}
          </PendingButton>
          {saved && !dirty && <SavedMark />}
        </>
      }
    >
      <SettingsField
        htmlFor="settings-org-name"
        label={translate("MC_ORG_SETTINGS_NAME_LABEL")}
        hint={
          name.trim() ? undefined : translate("MC_ORG_SETTINGS_NAME_REQUIRED")
        }
      >
        <Input
          id="settings-org-name"
          aria-invalid={!name.trim()}
          // Without it a screen reader announces "invalid" but not why.
          aria-describedby={
            name.trim() ? undefined : settingsHintId("settings-org-name")
          }
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </SettingsField>
      <SettingsField
        htmlFor="settings-org-address"
        label={translate("MC_ORG_SETTINGS_ADDRESS_LABEL")}
      >
        <Textarea
          id="settings-org-address"
          rows={3}
          value={address}
          onChange={(event) => setAddress(event.target.value)}
          placeholder={translate("MC_ORG_SETTINGS_ADDRESS_PLACEHOLDER")}
        />
      </SettingsField>
      {showBusinessRole && (
        <SettingsField
          htmlFor="settings-org-business-role"
          label={translate("SETTINGS/GENERAL/BUSINESS_ROLE_LABEL")}
          hint={translate("SETTINGS/GENERAL/BUSINESS_ROLE_HINT")}
        >
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger
              id="settings-org-business-role"
              aria-label={translate("SETTINGS/GENERAL/BUSINESS_ROLE_LABEL")}
              className="sm:max-w-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={BUSINESS_ROLE_NOT_SET}>
                {translate("SETTINGS/GENERAL/BUSINESS_ROLE_NOT_SET")}
              </SelectItem>
              {BUSINESS_ROLES.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {translate(label)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingsField>
      )}
    </SettingsSection>
  )
}

/** The language of the org's declarations of conformity and of the inquiry
 *  emails the KI-Assistent and follow-up questions create. */
function DocumentLanguageSection({
  saved,
}: {
  saved: InquiryTemplateSettings
}) {
  const { translate } = useTranslation()
  const saver = useTemplateSettingsSave()
  const [language, setLanguage] = useSavedDraft(saved.defaultLanguage)

  return (
    <SettingsSection
      title={translate("MC_TPL_DECL_LANGUAGE")}
      target="org.documentLanguage"
      // New inquiries start in it too: see newInquiryLanguage.
      description={translate("MC_TPL_DECL_LANGUAGE_HINT")}
      footer={
        <TemplateSaveButton
          saver={saver}
          saveKey="document-language"
          dirty={language !== saved.defaultLanguage}
          patch={() => ({ defaultLanguage: language })}
        />
      }
    >
      <Select
        value={language}
        onValueChange={setLanguage}
        disabled={!saver.canEdit}
      >
        <SelectTrigger
          aria-label={translate("MC_TPL_DECL_LANGUAGE")}
          className="sm:max-w-xs"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="de">
            {translate("MC_INQ_DRAFT_LANG_DE_FULL")}
          </SelectItem>
          <SelectItem value="en">
            {translate("MC_INQ_DRAFT_LANG_EN_FULL")}
          </SelectItem>
        </SelectContent>
      </Select>
    </SettingsSection>
  )
}

export function GeneralPage() {
  const { translate } = useTranslation()
  const { data: org, isLoading, isFetching, refetch } = useOrganization()
  // Document language (declarations and inquiries) and business role are for
  // every Material Compliance org.
  const materialCompliance = useMaterialComplianceOpen()

  return (
    <SettingsPage
      title={translate("SETTINGS/GENERAL/TITLE")}
      description={translate("SETTINGS/GENERAL/DESCRIPTION")}
      notice={translate("SETTINGS/GENERAL/NOTICE")}
    >
      {/* Cached data first: a failed background refetch must not replace a
          form that may hold unsaved edits. */}
      {org ? (
        <>
          <OrganizationDetailsSection
            key={org.id}
            org={org}
            showBusinessRole={materialCompliance}
          />
          <OrgLogoSection />
        </>
      ) : isLoading ? (
        <SettingsSection>
          <SettingsSkeleton />
        </SettingsSection>
      ) : (
        <SettingsSection>
          <SettingsLoadError
            pending={isFetching}
            onRetry={() => void refetch()}
          />
        </SettingsSection>
      )}
      {materialCompliance && (
        <TemplateSettingsLoader>
          {(settings) => <DocumentLanguageSection saved={settings} />}
        </TemplateSettingsLoader>
      )}
    </SettingsPage>
  )
}
